import { apiFetch } from '../../../shared/services/apiClient';
import {
  PaginadoBusqueda,
  CompararPreciosItem,
  PendienteMatching,
  SugerenciaMatching,
  ProductoCanonicoResponse,
  CrearCanonicoInput,
  OpcionesFiltros,
  CategoriaCanonicaFiltro,
  FiltrosCatalogo,
  ApiResponse,
  FiltrosDisponibles,
  ListadoParams,
  ListadoResponse,
  ProductoCatalogoPublico,
  ListadoMeta,
} from '../types';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

export class CatalogoService {
  static async buscarProductos(
    filtros: Partial<FiltrosCatalogo> & { texto?: string },
    pagina: number = 1,
    limite: number = 20
  ): Promise<PaginadoBusqueda> {
    const params = new URLSearchParams();
    params.set('pagina', String(pagina));
    params.set('limite', String(limite));

    if (filtros.texto && filtros.texto.trim()) {
      params.set('q', filtros.texto.trim());
    }
    if (filtros.proveedores && filtros.proveedores.length > 0) {
      filtros.proveedores.forEach((id) => params.append('proveedores[]', String(id)));
    }
    if (filtros.categoriaIds && filtros.categoriaIds.length > 0) {
      filtros.categoriaIds.forEach((id) => params.append('categoriaIds[]', String(id)));
    }
    if (filtros.subcategoriaIds && filtros.subcategoriaIds.length > 0) {
      filtros.subcategoriaIds.forEach((id) => params.append('subcategoriaIds[]', String(id)));
    }
    if (filtros.tags && filtros.tags.length > 0) {
      filtros.tags.forEach((tag) => params.append('tags[]', tag));
    }
    if (filtros.precioMin && filtros.precioMin.trim()) {
      params.set('precioMin', filtros.precioMin.trim());
    }
    if (filtros.precioMax && filtros.precioMax.trim()) {
      params.set('precioMax', filtros.precioMax.trim());
    }
    if (filtros.orden) {
      params.set('orden', filtros.orden);
    }

    const response = await apiFetch(`${API_URL}/catalogo/buscar?${params}`);
    const data: ApiResponse<PaginadoBusqueda> = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.error?.message || 'Error al buscar productos');
    }
    return data.data!;
  }

  static async obtenerFiltros(): Promise<OpcionesFiltros> {
    const response = await apiFetch(`${API_URL}/catalogo/filtros`);
    const data: ApiResponse<OpcionesFiltros> = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.error?.message || 'Error al obtener filtros');
    }
    return data.data!;
  }

  static async categoriasPorProveedores(ids: number[]): Promise<CategoriaCanonicaFiltro[]> {
    if (ids.length === 0) return [];
    const response = await apiFetch(`${API_URL}/catalogo/categorias-por-proveedores?ids=${ids.join(',')}`);
    const data: ApiResponse<CategoriaCanonicaFiltro[]> = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.error?.message || 'Error al obtener categorías');
    }
    return data.data!;
  }

  static async compararPrecios(canonicoId: number): Promise<CompararPreciosItem[]> {
    const response = await apiFetch(`${API_URL}/catalogo/canonicos/${canonicoId}/comparar`);
    const data: ApiResponse<CompararPreciosItem[]> = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.error?.message || 'Error al comparar precios');
    }
    return data.data!;
  }

  static async listarPendientes(): Promise<PendienteMatching[]> {
    const response = await apiFetch(`${API_URL}/catalogo/pendientes`);
    const data: ApiResponse<PendienteMatching[]> = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.error?.message || 'Error al listar pendientes');
    }
    return data.data!;
  }

  static async obtenerSugerencias(proveedorProductoId: number): Promise<SugerenciaMatching[]> {
    const response = await apiFetch(`${API_URL}/catalogo/pendientes/${proveedorProductoId}/sugerencias`);
    const data: ApiResponse<SugerenciaMatching[]> = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.error?.message || 'Error al obtener sugerencias');
    }
    return data.data!;
  }

  static async confirmarMatch(proveedorProductoId: number, canonicoId: number): Promise<void> {
    const response = await apiFetch(`${API_URL}/catalogo/pendientes/${proveedorProductoId}/confirmar`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ canonicoId }),
    });
    const data: ApiResponse<void> = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.error?.message || 'Error al confirmar match');
    }
  }

  static async crearCanonicoYMatch(
    proveedorProductoId: number,
    input: CrearCanonicoInput
  ): Promise<ProductoCanonicoResponse> {
    const response = await apiFetch(`${API_URL}/catalogo/pendientes/${proveedorProductoId}/crear-canonico`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    const data: ApiResponse<ProductoCanonicoResponse> = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.error?.message || 'Error al crear canónico');
    }
    return data.data!;
  }

  static async crearCanonico(input: CrearCanonicoInput): Promise<ProductoCanonicoResponse> {
    const response = await apiFetch(`${API_URL}/catalogo/canonicos`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    const data: ApiResponse<ProductoCanonicoResponse> = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.error?.message || 'Error al crear producto canónico');
    }
    return data.data!;
  }

  static async getListadoFiltros(proveedorIds?: number[]): Promise<FiltrosDisponibles> {
    const params = new URLSearchParams();
    if (proveedorIds && proveedorIds.length > 0) {
      proveedorIds.forEach((id) => params.append('proveedorIds', String(id)));
    }
    const qs = params.toString();
    const response = await apiFetch(`${API_URL}/catalogo/listado/filtros${qs ? '?' + qs : ''}`);
    const data: { success: boolean; data?: FiltrosDisponibles } = await response.json();
    if (!response.ok || !data.success) throw new Error('Error al obtener filtros del listado');
    return data.data!;
  }

  static async getListado(params: ListadoParams): Promise<ListadoResponse> {
    const qs = new URLSearchParams();
    if (params.cursor) qs.set('cursor', params.cursor);
    if (params.limite !== undefined) qs.set('limite', String(params.limite));
    if (params.orden) qs.set('orden', params.orden);
    if (params.q) qs.set('q', params.q);
    if (params.categoriaId !== undefined) qs.set('categoriaId', String(params.categoriaId));
    if (params.subcategoriaId !== undefined) qs.set('subcategoriaId', String(params.subcategoriaId));
    if (params.categoriaIds && params.categoriaIds.length > 0) {
      params.categoriaIds.forEach((id) => qs.append('categoriaIds', String(id)));
    }
    if (params.subcategoriaIds && params.subcategoriaIds.length > 0) {
      params.subcategoriaIds.forEach((id) => qs.append('subcategoriaIds', String(id)));
    }
    if (params.tags && params.tags.length > 0) {
      params.tags.forEach((t) => qs.append('tags', t));
    }
    if (params.precioMin !== undefined) qs.set('precioMin', String(params.precioMin));
    if (params.precioMax !== undefined) qs.set('precioMax', String(params.precioMax));
    if (params.conStock !== undefined) qs.set('conStock', String(params.conStock));
    if (params.color) qs.set('color', params.color);
    if (params.proveedorIds && params.proveedorIds.length > 0) {
      params.proveedorIds.forEach((id) => qs.append('proveedorIds', String(id)));
    }
    if (params.subcategoriasProveedor && params.subcategoriasProveedor.length > 0) {
      params.subcategoriasProveedor.forEach((s) => qs.append('subcategoriasProveedor', s));
    }
    if (params.categoriaProveedorIds && params.categoriaProveedorIds.length > 0) {
      params.categoriaProveedorIds.forEach((id) => qs.append('categoriaProveedorIds', String(id)));
    }
    const qstring = qs.toString();
    const response = await apiFetch(`${API_URL}/catalogo/listado${qstring ? '?' + qstring : ''}`);
    const data: { success: boolean; data?: ProductoCatalogoPublico[]; meta?: ListadoMeta } = await response.json();
    if (!response.ok || !data.success) throw new Error('Error al listar catálogo');
    return { data: data.data!, meta: data.meta! };
  }
}
